import React from "react"
import { Button, IconButton, Chip, Typography } from "@mui/material"
import { Plus, Trash2 } from "lucide-mui"
import { useFormikContext } from "formik"
import i18n from "@/i18n"
import type { FormValues } from "./types"

const STYLES = {
  root: "w-[280px] flex-shrink-0 overflow-y-auto",
  infoBox: "mb-4 bg-white border border-gray-200 rounded-lg p-3",
  infoTitle: "font-medium text-gray-700 mb-1",
  infoDescription: "text-gray-500",
  groupList: "space-y-4",
  groupCard: "bg-white rounded-lg border border-gray-200 overflow-hidden",
  groupHeader: "flex items-center justify-between p-3 border-b border-gray-100",
  groupNameInput: "font-medium text-gray-900 bg-transparent border-none outline-none flex-1",
  groupDeleteIcon: "w-4 h-4 text-gray-400",
  groupTablesWrapper: "p-3",
  tablesChipRow: "flex flex-wrap gap-2",
  tableChipDeleteIcon: "w-3 h-3",
  tableChip: "bg-gray-100",
  addTableButton: "mt-2 text-gray-500",
  addTableIcon: "w-4 h-4",
  addGroupButton:
    "w-full border-2 border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center gap-2 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors",
  addGroupIcon: "w-4 h-4",
  addGroupLabel: "text-sm",
} as const

type SectionsListProps = {
  onRemoveTableGroup: (groupIndex: number) => void
  onRemoveTable: (groupIndex: number, tableIndex: number) => void
  onAddTableGroup: () => void
  onAddTable: (groupIndex: number) => void
}

const SectionsList: React.FC<SectionsListProps> = ({
  onRemoveTableGroup,
  onRemoveTable,
  onAddTableGroup,
  onAddTable,
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>()

  return (
    <div className={STYLES.root}>
      <div className={STYLES.infoBox}>
        <Typography variant="body2" className={STYLES.infoTitle}>
          {i18n.t("app.settings.venues.venue_layout.organize_tables_title")}
        </Typography>
        <Typography variant="caption" className={STYLES.infoDescription}>
          {i18n.t("app.settings.venues.venue_layout.organize_tables_description")}
        </Typography>
      </div>

      <div className={STYLES.groupList}>
        {values.tableGroups.map((group, groupIndex) => (
          <div key={groupIndex} className={STYLES.groupCard} data-testid={`settings-venue-layout-group-${groupIndex}`}>
            <div className={STYLES.groupHeader}>
              <input
                type="text"
                value={group.name}
                onChange={(e) => setFieldValue(`tableGroups[${groupIndex}].name`, e.target.value)}
                className={STYLES.groupNameInput}
              />
              <IconButton
                size="small"
                onClick={() => onRemoveTableGroup(groupIndex)}
                data-testid={`settings-venue-layout-group-${groupIndex}-btn-remove`}
              >
                <Trash2 className={STYLES.groupDeleteIcon} />
              </IconButton>
            </div>

            <div className={STYLES.groupTablesWrapper}>
              <div className={STYLES.tablesChipRow}>
                {group.tables.map((table, tableIndex) => (
                  <Chip
                    key={tableIndex}
                    label={table.name}
                    size="small"
                    onDelete={() => onRemoveTable(groupIndex, tableIndex)}
                    deleteIcon={<Trash2 className={STYLES.tableChipDeleteIcon} />}
                    className={STYLES.tableChip}
                    data-testid={`settings-venue-layout-group-${groupIndex}-table-${tableIndex}`}
                  />
                ))}
              </div>
              <Button
                size="small"
                startIcon={<Plus className={STYLES.addTableIcon} />}
                onClick={() => onAddTable(groupIndex)}
                className={STYLES.addTableButton}
                data-testid={`settings-venue-layout-group-${groupIndex}-btn-add-table`}
              >
                {i18n.t("app.settings.venues.venue_layout.add_table")}
              </Button>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={onAddTableGroup}
          className={STYLES.addGroupButton}
          data-testid="settings-venue-layout-btn-add-group"
        >
          <Plus className={STYLES.addGroupIcon} />
          <span className={STYLES.addGroupLabel}>{i18n.t("app.settings.venues.venue_layout.add_new_table_group")}</span>
        </button>
      </div>
    </div>
  )
}

export default SectionsList
